import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, Sparkle } from "lucide-react";
import { PROFILE } from "../data/portfolio";
import { CountUp } from "./CountUp";

const HERO_BG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBuZXVyYWwlMjBuZXR3b3JrJTIwdGVjaHxlbnwwfHx8fDE3ODA5ODU4MDV8MA&ixlib=rb-4.1.0&q=85";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Parallax: bg drifts up, foreground gently fades + lifts on scroll
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen bg-[#050505] text-white overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div className="absolute inset-0 overflow-hidden" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute inset-0 grid-line opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
      </motion.div>

      {/* Foreground content (lifts + fades as you scroll) */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-36 pb-20"
      >
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-12 gap-y-10 gap-x-0 lg:gap-10 items-end"
        >
          {/* LEFT */}
          <div className="col-span-12 lg:col-span-8">
            <motion.div
              variants={fadeUp}
              className="text-3xl inline-block origin-bottom-left"
              animate={{ rotate: [0, 14, -8, 14, 0] }}
              transition={{ duration: 1.6, delay: 0.6, ease: "easeInOut" }}
              aria-hidden
            >
              👋
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="mt-6 leading-[1] tracking-tight"
            >
              <motion.span
                variants={fadeUp}
                className="block font-display font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] italic tracking-tighter text-white/85"
              >
                Hello!
              </motion.span>
              <motion.span
                variants={fadeUp}
                className="block font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] tracking-tighter mt-2"
              >
                I&apos;m Satyam
              </motion.span>
            </h1>

            <motion.div
              variants={fadeUp}
              data-testid="hero-role-badge"
              className="mt-8 flex items-center gap-3"
            >
              <span className="h-px w-12 bg-white/40" />
              <span className="font-display text-xl sm:text-2xl tracking-tight">
                AI Engineer
              </span>
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="inline-flex"
              >
                <Sparkle size={18} className="text-white/80" />
              </motion.span>
              <span className="h-px w-16 bg-white/40" />
            </motion.div>

            <motion.p
              variants={fadeUp}
              data-testid="hero-description"
              className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-white/75"
            >
              Hello! I&apos;m Satyam Garhewal. I&apos;m an{" "}
              <strong className="font-bold text-white">
                AI Engineer, generative AI builder, ML practitioner
              </strong>
              , backend architect & full-stack engineer with{" "}
              <strong className="font-bold text-white">7+ years</strong>{" "}
              shipping production software.
            </motion.p>

            <motion.ul
              variants={stagger}
              data-testid="hero-bullets"
              className="mt-10 space-y-4 max-w-md"
            >
              {[
                "Generative AI & LLM systems in production",
                "Backends that scale on AWS",
                "End-to-end ownership across stack",
              ].map((b) => (
                <motion.li
                  key={b}
                  variants={fadeUp}
                  className="flex items-center gap-4"
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 border border-white/60 rounded-full">
                    <Check size={12} strokeWidth={2.4} className="text-white" />
                  </span>
                  <span className="text-sm sm:text-base text-white/80">{b}</span>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              variants={fadeUp}
              className="mt-12 flex flex-wrap items-center gap-6"
            >
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                data-testid="hero-cta-talk"
                onClick={() => scrollTo("contact")}
                className="inline-flex items-center gap-3 bg-white text-black pl-6 pr-4 py-3 hover:bg-neutral-200 transition-colors"
              >
                <span className="font-mono text-xs uppercase tracking-[0.2em]">
                  Let&apos;s Talk
                </span>
                <span className="inline-flex items-center justify-center w-7 h-7 bg-black text-white">
                  <ArrowUpRight size={14} />
                </span>
              </motion.button>

              <motion.a
                whileHover={{ x: 4 }}
                data-testid="hero-cta-medium"
                href={PROFILE.medium}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex flex-col items-start"
              >
                <span className="flex items-center gap-2 text-sm text-white">
                  Read on Medium
                  <ArrowUpRight
                    size={16}
                    className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                  />
                </span>
                <span className="mt-1 h-px w-full bg-white" />
              </motion.a>
            </motion.div>
          </div>

          {/* RIGHT — portrait + stats */}
          <motion.div
            variants={fadeUp}
            className="col-span-12 lg:col-span-4 flex flex-col gap-6"
            data-testid="hero-stats"
          >
            {/* Portrait */}
            <div className="relative h-72 sm:h-96 lg:h-auto lg:aspect-[3/4] overflow-hidden border border-white/10 bg-[#0a0a0a]">
              <img
                src="/satyam-portrait.png"
                alt={PROFILE.name}
                className="w-full h-full object-cover object-[center_15%] lg:object-center grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
              {/* Corner ticks */}
              <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-white/40" />
              <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-white/40" />
              <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-white/40" />
              <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-white/40" />
              {/* Stats overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-4 flex justify-between items-end">
                <div>
                  <p className="label-eyebrow text-white/60">Experience</p>
                  <p className="font-display font-bold text-2xl tracking-tighter mt-1">
                    <CountUp to={7} suffix="+ yrs" />
                  </p>
                </div>
                <div className="text-right">
                  <p className="label-eyebrow text-white/60">Status</p>
                  <p className="font-mono-ui text-xs text-white/80 mt-1 flex items-center gap-1.5 justify-end">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-60" />
                      <span className="relative inline-block w-2 h-2 bg-emerald-400 rounded-full" />
                    </span>
                    {PROFILE.available}
                  </p>
                </div>
              </div>
            </div>
            {/* Companies row */}
            <div className="lg:border-l lg:border-white/10 lg:pl-4">
              <p className="label-eyebrow">Companies</p>
              <p className="font-mono-ui text-sm text-white/80 mt-2 leading-relaxed">
                EXL · Brillio
                <br />
                Firstcry · Valtech
              </p>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Bottom marker with bouncing scroll indicator */}
      <div className="absolute bottom-6 left-6 md:left-12 right-6 md:right-12 flex items-center justify-between label-eyebrow">
        <motion.span
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center gap-2"
        >
          scroll ↓
        </motion.span>
        <span>v1.0 · 2026</span>
      </div>
    </section>
  );
};

export default Hero;
